/**
 * Per-browser persistence for the account-rail collapsed toggle.
 *
 * WHY a dedicated boolean and not just `autoSaveId`:
 * `react-resizable-panels`' `autoSaveId="icgchat-columns"` already persists the
 * whole layout — and a collapsed panel is stored as size `0` in that layout's
 * `layout` array, so on reload the panel DOES come back collapsed on its own
 * (verified against the 3.0.6 source: `savePanelGroupState` writes
 * `{ expandToSizes, layout }`, and mount reads `unsafeLayout = state.layout`).
 *
 * But two things the layout array does NOT give us cleanly:
 *   1. The React `accountsCollapsed` boolean that drives the toggle button's
 *      aria-label / icon needs a correct value on the FIRST paint. The panel's
 *      `onCollapse` callback is not guaranteed to fire for the initial
 *      restored-collapsed state, so reading a boolean here seeds that state
 *      deterministically (no flash of the wrong label).
 *   2. A single source of truth we control, independent of the library's
 *      internal storage schema, that Layout's mount effect reconciles the
 *      panel against — so the button and the panel can never disagree.
 *
 * Guarded with try/catch like `theme-migration.ts` / `store/auth.ts`:
 * `localStorage` throws in private-mode Safari and other restricted contexts;
 * a persistence miss must never break the console, so we degrade to the
 * in-memory default (expanded) rather than throw.
 */

/** localStorage key holding the account-rail collapsed boolean. */
export const ACCOUNTS_COLLAPSED_STORAGE_KEY = 'icgchat-accounts-collapsed';

/**
 * Read the persisted collapsed flag. Returns `false` (expanded) when unset,
 * when the value isn't the literal `'1'`, or when storage is unavailable.
 */
export function readAccountsCollapsed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(ACCOUNTS_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist the collapsed flag (`'1'` when collapsed, key removed when not). */
export function writeAccountsCollapsed(collapsed: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (collapsed) {
      window.localStorage.setItem(ACCOUNTS_COLLAPSED_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(ACCOUNTS_COLLAPSED_STORAGE_KEY);
    }
  } catch {
    // Storage disabled/unavailable — collapse still works this session via the
    // in-memory React state; it just won't survive a reload.
  }
}
